/**
 * GPX parsing utilities
 */

/**
 * Load GPX route data from URL
 * @param {string} routeUrl - URL to GPX file
 * @returns {Promise<Array>} Array of route points [{latitude, longitude}]
 */
export async function loadGpxRoute(routeUrl) {
  if (!routeUrl) {
    return []
  }
  
  try {
    const response = await fetch(routeUrl)
    if (!response.ok) {
      throw new Error(`Failed to load GPX: ${response.status}`)
    }
    
    const gpxText = await response.text()
    return parseGpxToRoute(gpxText)
  } catch (error) {
    console.warn('Error loading GPX route:', error)
    return []
  }
}

/**
 * Parse GPX XML text to route points
 * @param {string} gpxText - GPX XML content
 * @returns {Array} Array of route points
 */
export function parseGpxToRoute(gpxText) {
  try {
    const parser = new DOMParser()
    const gpxDoc = parser.parseFromString(gpxText, 'text/xml')
    
    // Check for parsing errors
    const parseError = gpxDoc.querySelector('parsererror')
    if (parseError) {
      throw new Error('Invalid GPX format')
    }
    
    const route = []
    
    // Try to get track points first (trkpt)
    const trackPoints = gpxDoc.querySelectorAll('trkpt')
    if (trackPoints.length > 0) {
      trackPoints.forEach(point => {
        const routePoint = extractRoutePoint(point)
        if (routePoint) route.push(routePoint)
      })
    } else {
      // Fallback to route points (rtept) if no track points
      const routePoints = gpxDoc.querySelectorAll('rtept')
      routePoints.forEach(point => {
        const routePoint = extractRoutePoint(point)
        if (routePoint) route.push(routePoint)
      })
    }
    
    return route
  } catch (error) {
    console.warn('Error parsing GPX:', error)
    return []
  }
}

/**
 * Extract route point from XML element
 * @param {Element} point - XML point element
 * @returns {Object|null} Route point or null if invalid
 */
function extractRoutePoint(point) {
  const lat = parseFloat(point.getAttribute('lat'))
  const lon = parseFloat(point.getAttribute('lon'))
  
  if (isNaN(lat) || isNaN(lon)) {
    return null
  }
  
  const routePoint = { latitude: lat, longitude: lon }
  
  // Extract elevation data if available
  const eleElement = point.querySelector('ele')
  if (eleElement && eleElement.textContent) {
    const altitude = parseFloat(eleElement.textContent)
    if (!isNaN(altitude)) {
      routePoint.altitude = altitude
    }
  }
  
  return routePoint
}